import { ChangeDetectionStrategy, Component, inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { DialogData } from '../../../../shared/models/dialog-data.model';
import { MatButtonModule } from '@angular/material/button';
import { InputField } from '../../../../shared/components/input-field/input-field';
import { DNSRecordType } from '../../../../shared/models/dns.models';
import { Control } from './add-dns-record-dialog.models';
import {
  BASE_CONTROLS,
  CAA_CONTROLS,
  MX_CONTROLS,
  SRV_CONTROLS,
  TYPE_CONFIGS,
  TYPES,
} from './add-dns-record-dialog.const';
import { map, startWith, Subject, takeUntil } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { DnsRecordFormValidator } from './dns-record-form-validator';
import { MatRadioModule } from '@angular/material/radio';

@Component({
  selector: 'app-add-dns-record-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    InputField,
    AsyncPipe,
    MatRadioModule,
  ],
  templateUrl: './add-dns-record-dialog.component.html',
  styleUrl: './add-dns-record-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddDnsRecordDialogComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject(MatDialogRef<AddDnsRecordDialogComponent>);

  readonly data = inject<DialogData & { domain: string }>(MAT_DIALOG_DATA);

  private fb = inject(FormBuilder);

  form = this.fb.nonNullable.group(
    {
      type: [DNSRecordType.A, [Validators.required]],
      host: [''],
      target: ['', [Validators.required]],
      ttl: [null],
    },
    {
      validators: [DnsRecordFormValidator.create()],
    },
  );

  types = TYPES;

  destroyed$ = new Subject();

  currentControls$ = this.form.controls.type.valueChanges.pipe(
    startWith(DNSRecordType.A),
    takeUntil(this.destroyed$),
    map((type) => this.getShownControls(type)),
  );

  get hostSuffix(): string {
    return '.' + this.data.subTitle;
  }

  ngOnInit(): void {
    this.form.controls.type.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((type) => {
      this.removeDynamicControls();
      if (this.isDNSRecordType(type)) {
        const config = TYPE_CONFIGS[type];
        if (config) {
          // Add missing controls
          Object.entries(config).forEach(([name, definition]) => {
            if (!this.form.contains(name)) {
              (this.form as FormGroup).addControl(name, this.fb.control(...definition));
            }
          });
        }
      }
      this.form.updateValueAndValidity();
    });
  }

  getShownControls(type: string): Control[] {
    switch (type) {
      case DNSRecordType.MX:
        return MX_CONTROLS;
      case DNSRecordType.SRV:
        return SRV_CONTROLS;
      case DNSRecordType.CAA:
        return CAA_CONTROLS;
      default:
        return BASE_CONTROLS;
    }
  }

  submit(): void {
    if (this.form.invalid) return;

    const value = this.form.getRawValue();
    value.host = this.buildRecordHost(value.host, this.data.domain);

    this.dialogRef.close(value);
  }

  close(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.destroyed$.next('');
    this.destroyed$.complete();
  }

  private isDNSRecordType(value: string): value is DNSRecordType {
    return Object.values(DNSRecordType).includes(value as DNSRecordType);
  }

  private removeDynamicControls(): void {
    Object.keys(this.form.controls)
      .filter((controlName) => !BASE_CONTROLS.map((c) => c.controlName).includes(controlName))
      .forEach((controlName) => {
        (this.form as FormGroup).removeControl(controlName);
      });
  }

  private buildRecordHost(hostInput: string, domainInput: string): string {
    const host = (hostInput || '').trim().toLowerCase();
    const domain = domainInput.trim().toLowerCase();

    if (!host || host === '@' || host === domain) {
      return domain;
    }

    if (host.endsWith(`.${domain}`)) {
      return host;
    }

    return `${host}.${domain}`;
  }
}
